import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener('click', this.#handleOutsideClick);
    document.removeEventListener('keydown', this.#handleEscapeKey);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  #handleOutsideClick = (event) => {
    const { dialog } = this.refs;
    if (!dialog.open) return;
    const rect = dialog.getBoundingClientRect();
    const isOutside =
      event.clientX < rect.left ||
      event.clientX > rect.right ||
      event.clientY < rect.top ||
      event.clientY > rect.bottom;
    if (isOutside) this.closeDialog();
  };

  #handleEscapeKey = (event) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.closeDialog();
  };

  showDialog() {
    const { dialog } = this.refs;
    if (dialog.open) return;

    dialog.show();
    this.dispatchEvent(new DialogOpenEvent());

    setTimeout(() => {
      document.addEventListener('click', this.#handleOutsideClick);
      document.addEventListener('keydown', this.#handleEscapeKey);
    });
  }

  closeDialog = async () => {
    const { dialog } = this.refs;
    if (!dialog.open) return;

    document.removeEventListener('click', this.#handleOutsideClick);
    document.removeEventListener('keydown', this.#handleEscapeKey);

    dialog.classList.add('dialog-closing');

    await onAnimationEnd(dialog, undefined, { subtree: false });

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
